import { useSwrMutationApi } from '@hooks/useSwrApi';
import { useMemo } from 'react';

interface GenerateInterviewQuestionsPayload {
  // No payload needed - job details are fetched from database using jobId
}

interface GenerateInterviewQuestionsResponse {
  questions: string[];
}

interface UseSwrGenerateInterviewQuestionsProps {
  locale?: string;
  jobId: number;
  onSuccess?: (response: GenerateInterviewQuestionsResponse) => void;
  onError?: (error: unknown) => void;
}

/**
 * @description SWR mutation: generate interview questions from job ID (fetches job details from DB)
 *
 * @example
 * const { trigger, isMutating } = useSwrGenerateInterviewQuestions({
 *   locale: 'ro',
 *   jobId: 1234,
 *   onSuccess: (response) => console.log('Questions generated:', response.questions),
 *   onError: (error) => console.error('Failed to generate questions')
 * });
 *
 * // Trigger the generation (no payload needed)
 * void trigger();
 */
export const useSwrGenerateInterviewQuestions = (props: UseSwrGenerateInterviewQuestionsProps) => {
  const {
    locale = 'ro', jobId, onSuccess, onError,
  } = props;

  // Build the URL with jobId path parameter
  const url = useMemo(
    () => `/v1/interview-questions/${jobId}/generate-by-id`,
    [jobId],
  );

  const {
    data,
    error,
    trigger,
    isMutating,
    reset,
  } = useSwrMutationApi<GenerateInterviewQuestionsResponse, GenerateInterviewQuestionsPayload>({
    url,
    locale,
    fetcherConfig: {
      method: 'POST',
      stringifyPayload: true,
      headers: {
        'Content-Type': 'application/json',
      },
    },
    apiOptions: {
      onSuccess: (response) => {
        if (onSuccess) {
          onSuccess(response);
        }
      },
      onError: (err) => {
        if (onError) {
          onError(err);
        }
      },
    },
    toastsOptions: {
      showErrorToast: true,
    },
  });

  return {
    data,
    error,
    trigger,
    isMutating,
    reset,
  };
};
